import { UserPlus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../api/client';
import type { PermissionDef, StaffPayload, StaffRow } from '../api/types';
import { ago } from '../lib/format';
import { useMe } from '../lib/me';
import { useLoad } from '../lib/useLoad';
import { useDialog } from '../lib/useDialog';

/**
 * Who can sign in to this console, and what each of them may open and do.
 *
 * Everything here is enforced by the server (routes/staff.js), and this screen
 * only draws it: root cannot be edited, nobody edits themselves, only addresses
 * in the domain can be added, and an editor can only grant -- or take away --
 * permissions they hold themselves. A box the reader cannot move is drawn
 * disabled with the reason, rather than missing, so "why can't I give her
 * Audit?" answers itself.
 */

type Editing = { mode: 'add' } | { mode: 'edit'; row: StaffRow };

function accessLabel(row: StaffRow, payload: StaffPayload) {
  if (row.isRoot) return 'Everything (root)';
  const preset = payload.presets.find((entry) => entry.key === row.preset);
  if (preset) return preset.label;
  const screens = row.permissions.filter((key) => payload.catalog.find((p) => p.key === key)?.kind === 'screen');
  const actions = row.permissions.length - screens.length;
  return `Custom · ${screens.length} screen${screens.length === 1 ? '' : 's'}, ${actions} action${actions === 1 ? '' : 's'}`;
}

function StatusBadge({ row }: { row: StaffRow }) {
  if (!row.enabled) return <span className="badge badge-danger">Off</span>;
  if (!row.signedIn) {
    return (
      <span className="badge badge-warn" title="Added, but has not signed in yet">
        Invited
      </span>
    );
  }
  return <span className="badge badge-ok">Active</span>;
}

/**
 * Turning one box on or off, with the catalog's rule applied both ways: an
 * action brings its screen with it, and taking a screen away takes the actions
 * done from it -- the server would do the first anyway, and the second is what
 * the person unticking the screen means.
 */
function toggle(selected: Set<string>, permission: PermissionDef, catalog: PermissionDef[], on: boolean) {
  const next = new Set(selected);
  if (on) {
    next.add(permission.key);
    for (const needed of permission.requires ?? []) next.add(needed);
  } else {
    next.delete(permission.key);
    for (const other of catalog) if (other.requires?.includes(permission.key)) next.delete(other.key);
  }
  return next;
}

function Editor({
  editing,
  payload,
  onClose,
  onSaved,
}: {
  editing: Editing;
  payload: StaffPayload;
  onClose: () => void;
  onSaved: () => void;
}) {
  const me = useMe();
  const row = editing.mode === 'edit' ? editing.row : null;
  const locked = Boolean(row && (row.isRoot || row.id === payload.you));
  const mine = new Set(me?.permissions ?? []);

  const [local, setLocal] = useState('');
  const [name, setName] = useState(row?.name ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(row ? row.permissions : (payload.presets.find((p) => p.key === 'viewer')?.permissions ?? [])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Focus in on open, Tab kept inside, Escape closes, focus back on close.
  const panel = useRef<HTMLElement>(null);
  useDialog(panel, onClose);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  // Typed without the domain: the field cannot hold an address the server would
  // refuse, and pasting a whole address still works.
  const typed = local.trim().toLowerCase();
  const email = typed.includes('@') ? typed : `${typed}@${payload.domain}`;
  // The server's rule (services/staff.js cleanEmail): one `@`, and exactly the
  // domain after it.
  const emailOk = /^[^\s@]+@[^\s@]+$/.test(email) && email.slice(email.lastIndexOf('@') + 1) === payload.domain;

  const save = () =>
    run(() =>
      row
        ? api.post(`/staff/${row.id}`, {
            permissions: [...selected],
            ...(name.trim() !== (row.name ?? '') ? { name } : {}),
          })
        : api.post('/staff', { email, name, permissions: [...selected] }),
    );

  const preset = payload.presets.find(
    (entry) => entry.permissions.length === selected.size && entry.permissions.every((key) => selected.has(key)),
  );
  const screens = payload.catalog.filter((p) => p.kind === 'screen');
  const actions = payload.catalog.filter((p) => p.kind === 'action');

  const box = (permission: PermissionDef) => {
    const grantable = !locked && mine.has(permission.key);
    return (
      <label
        key={permission.key}
        className={`wt-perm${grantable ? '' : ' wt-perm-off'}`}
        title={
          locked
            ? undefined
            : grantable
              ? permission.help
              : 'You do not hold this permission, so you can neither grant it nor take it away.'
        }
      >
        <input
          type="checkbox"
          checked={row?.isRoot || selected.has(permission.key)}
          disabled={!grantable || busy}
          onChange={(event) => setSelected(toggle(selected, permission, payload.catalog, event.target.checked))}
        />
        <span>
          <span className="wt-perm-label">{permission.label}</span>
          <span className="wt-perm-help">{permission.help}</span>
        </span>
      </label>
    );
  };

  return (
    <>
      <div className="wt-drawer-backdrop" onClick={onClose} />
      <aside ref={panel} className="wt-drawer wt-users-drawer" role="dialog" aria-modal="true" aria-label="User">
        <header className="wt-drawer-head">
          <div className="wt-drawer-titles">
            <div className="wt-drawer-title">{row ? row.name || row.email : 'Add a user'}</div>
            <div className="wt-drawer-sub">
              {row
                ? `${row.email} · added ${row.createdBy ? `by ${row.createdBy} ` : ''}${ago(row.createdAt)}`
                : `They sign in with their @${payload.domain} Google account.`}
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" title="Close (Esc)" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        {row?.isRoot && (
          <p className="note">
            Root, because this address is in WATCH_STAFF_EMAILS on the server. Root holds every permission, now and
            later, and is given or taken away there only — not from this screen.
          </p>
        )}
        {row && !row.isRoot && row.id === payload.you && (
          <p className="note">This is you. Your own access can only be changed by another admin.</p>
        )}

        <div className="wt-users-fields">
          {!row && (
            <label className="field">
              <span className="field-label">Email</span>
              <span className="wt-email">
                <input
                  className="input"
                  placeholder="firstname"
                  value={local}
                  // biome-ignore lint/a11y/noAutofocus: the field appears because "Add" was just pressed; focus belongs in it
                  autoFocus
                  aria-invalid={Boolean(typed) && !emailOk}
                  onChange={(event) => setLocal(event.target.value)}
                />
                {!typed.includes('@') && <span className="wt-email-domain">@{payload.domain}</span>}
              </span>
              {typed && !emailOk && <span className="field-error">Only @{payload.domain} addresses can be added.</span>}
            </label>
          )}
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              placeholder="Filled in from Google on their first sign-in"
              value={name}
              disabled={locked || busy}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        </div>

        {!locked && (
          <div className="wt-presets">
            <span className="field-label">Start from</span>
            {payload.presets.map((entry) => {
              const grantable = entry.permissions.every((key) => mine.has(key));
              return (
                <button
                  key={entry.key}
                  type="button"
                  className={`btn ${preset?.key === entry.key ? 'btn-primary' : 'btn-secondary'}`}
                  title={grantable ? entry.help : `${entry.help} Includes permissions you do not hold.`}
                  disabled={!grantable || busy}
                  onClick={() => setSelected(new Set(entry.permissions))}
                >
                  {entry.label}
                </button>
              );
            })}
            {!preset && <span className="badge">custom</span>}
          </div>
        )}

        <h3 className="wt-users-group">Screens they can open</h3>
        <div className="wt-perms">{screens.map(box)}</div>
        <h3 className="wt-users-group">What they can do</h3>
        <div className="wt-perms wt-perms-actions">{actions.map(box)}</div>

        {error && <p className="note note-danger">{error}</p>}

        {!locked && (
          <div className="wt-users-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || selected.size === 0 || (!row && !emailOk)}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : row ? 'Save' : 'Add user'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <span className="wt-spacer" />
            {row && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => void run(() => api.post(`/staff/${row.id}`, { enabled: !row.enabled }))}
              >
                {row.enabled ? 'Turn access off' : 'Turn access on'}
              </button>
            )}
            {row &&
              (confirmRemove ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={busy}
                  onClick={() => void run(() => api.del(`/staff/${row.id}`))}
                >
                  Remove {row.email}?
                </button>
              ) : (
                <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmRemove(true)}>
                  Remove
                </button>
              ))}
          </div>
        )}
        {!locked && selected.size === 0 && (
          <p className="wt-drawer-foot">Give them at least one screen — or turn their access off instead.</p>
        )}
      </aside>
    </>
  );
}

export function Users() {
  const { data, error, loading, reload } = useLoad(() => api.get<StaffPayload>('/staff'), []);
  const [editing, setEditing] = useState<Editing | null>(null);

  if (loading) return <div className="wt-empty">Loading…</div>;
  if (error || !data) return <div className="wt-banner wt-banner-danger">{error?.message ?? 'Could not load users.'}</div>;

  const active = data.staff.filter((row) => row.enabled).length;

  return (
    <>
      <div className="wt-head">
        <h1>Users</h1>
        <span className="sub">
          {active} with access · @{data.domain} addresses only, by Google or a password · root is whoever
          WATCH_STAFF_EMAILS lists
        </span>
        <span className="wt-spacer" />
        <button type="button" className="btn btn-primary" onClick={() => setEditing({ mode: 'add' })}>
          <UserPlus size={14} /> Add user
        </button>
      </div>

      <div className="wt-table-wrap">
        <table className="wt-table wt-stack">
          <thead>
            <tr>
              <th>Person</th>
              <th>Access</th>
              <th>Status</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {data.staff.map((row) => (
              <tr
                key={row.id}
                className="wt-row-click"
                tabIndex={0}
                style={row.enabled ? undefined : { opacity: 0.6 }}
                onClick={() => setEditing({ mode: 'edit', row })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') setEditing({ mode: 'edit', row });
                }}
              >
                <td className="wt-stack-wide">
                  <div>
                    {row.name || row.email.split('@')[0]}
                    {row.isRoot && <span className="badge badge-accent wt-users-tag">root</span>}
                    {row.id === data.you && <span className="badge wt-users-tag">you</span>}
                    {row.password && (
                      <span
                        className="badge wt-users-tag"
                        title={`Can also sign in with a sphere-watch password and an emailed code${
                          row.passwordSetAt ? ` (set ${ago(row.passwordSetAt)})` : ''
                        }. They set it themselves; turning their access off ends those sign-ins.`}
                      >
                        password
                      </span>
                    )}
                  </div>
                  <div className="wt-mono">{row.email}</div>
                </td>
                <td data-label="Access">{accessLabel(row, data)}</td>
                <td data-label="Status">
                  <StatusBadge row={row} />
                </td>
                <td className="wt-when" data-label="Last seen">{row.lastSeenAt ? ago(row.lastSeenAt) : 'never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Editor
          key={editing.mode === 'edit' ? editing.row.id : 'add'}
          editing={editing}
          payload={data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </>
  );
}
